// ./src/pages/private/EventGamesPage/GameDrawer.tsx
//
// Configure a live party game (#34): its name, a preset (which prefills the
// roles), when secret roles reveal, and the ROLES/markers themselves — each with a
// color/icon, a visibility (secret / others-see-it / public) and a count rule that
// scales with the player count (fixed / ratio / rest). Dealing rounds happens
// separately (GamePlayDrawer). URL-driven (add / :id/edit).

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Stack, Group, Text, Box, Paper, ActionIcon, Divider, SimpleGrid, Collapse, UnstyledButton, Switch } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  EventGameService,
  EventGameMarkerService,
  EventGame,
  EventGameInput,
  EventGameMarker,
  EventGameMarkerInput,
  EventGameComposition,
  EventGameCompositionType,
  EventGameMarkerVisibility,
  EventGameRevealRule,
  EventGameAssignmentKind,
} from "@/utils/api";
import { getFormErrors, runBulkWrites, refusalReason } from "@/utils";
import { useFormSnapshot } from "@/hooks";
import { AppDrawer, AppButton, AppIcon, TextField, TextAreaField, NumberField, SelectField, ColorPickerInput } from "@/components/generic";
import { GAME_PRESETS, findPreset } from "./presets";

export interface GameDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /** null = create a new game; otherwise edit this one. */
  game: EventGame | null;
  onSaved: () => void;
  /**
   * A NEW game starts from this preset (the create wizard's "Secret Santa" for a Christmas party). Applied through the
   * same `withPreset` the preset picker uses, so the two can never build a different game. Ignored on edit.
   */
  defaultPresetId?: string | null;
}

interface MarkerForm {
  id?: string;
  name: string;
  color: string;
  icon: string;
  team: string;
  visibility: EventGameMarkerVisibility;
  compType: EventGameCompositionType;
  compValue: number; // fixed → count; ratio → percent (0-100)
  // Optional character dossier — edited as RAW text (converted to marker.payload only
  // at save, so typing spaces/blank lines is never normalized mid-keystroke). Objectives
  // + relationships are one item per line. `payloadExtra` preserves any non-dossier keys.
  dFlavor: string;
  dBackstory: string;
  dSecret: string;
  dObjectives: string;
  dRelationships: string;
  // Shared-secret games (Spyfall / Undercover): the per-round pool of locations / word
  // pairs on this marker's payload. Edited as raw text — one topic per line; for keyed
  // pools (Undercover) each line is the slot values in `dSecretPoolKeys` order, "|"-split.
  dSecretPool: string;
  dSecretPoolKeys: string[]; // [] = plain-string pool; non-empty = keyed (e.g. ["civilian","undercover"])
  hasPool: boolean; // this marker owns a secret pool (drives whether the pool editor shows)
  // Who-Am-I: a pool of identities (one per line), dealt one-per-player. Owned by an "others"-visibility marker.
  dIdentityPool: string;
  hasIdentityPool: boolean;
  payloadExtra: Record<string, unknown> | null;
}

interface GameForm {
  name: string;
  description: string;
  // Self-mark checklist (Human Bingo / scavenger). `checklistText` = one prompt per line;
  // `checklistIds` keeps ids stable across edits (position-matched) so progress maps.
  checklistText: string;
  checklistIds: string[];
  allowReuse: boolean;
  preset: string;
  // "role" (deal a marker) | "target" (Secret Santa) | "pair" (Networking Roulette).
  assignmentKind: EventGameAssignmentKind;
  // Target/pair games: never match a guest from your own rsvp_group (a family).
  excludeHousehold: boolean;
  // Pair games only (Networking Roulette): prefer partners who share interests.
  matchInterests: boolean;
  revealRule: EventGameRevealRule;
  notes: string;
  markers: MarkerForm[];
}

const KNOWN_FORM_FIELDS = ["name", "description"];

const EMPTY_MARKER: MarkerForm = {
  name: "", color: "#6d4aff", icon: "", team: "", visibility: "self", compType: "rest", compValue: 0,
  dFlavor: "", dBackstory: "", dSecret: "", dObjectives: "", dRelationships: "",
  dSecretPool: "", dSecretPoolKeys: [], hasPool: false, dIdentityPool: "", hasIdentityPool: false, payloadExtra: null,
};

// ── Dossier (marker.payload) helpers ─────────────────────────────────────────
// The dossier is snapshotted onto each dealt assignment and shown on the guest's
// "My Role" block. `flavor/backstory/secret` are strings; `objectives/relationships`
// are string[] edited one item per line. Known keys are split out of `payload` into
// the raw text fields on load; any OTHER keys are preserved in `payloadExtra`.
// `secretPool` is pulled out of the payload separately (it has its own editor), so it's
// NOT in `payloadExtra` — but its scalar siblings (secretLabel / secretKey) are preserved there.
const DOSSIER_KEYS = ["flavor", "backstory", "secret", "objectives", "relationships"];
const POOL_KEYS = ["secretPool", "identityPool"];
const linesToList = (s: string): string[] => s.split("\n").map((x) => x.trim()).filter(Boolean);
const listToLines = (v: unknown): string => (Array.isArray(v) ? v.map((x) => String(x)) : []).join("\n");
const payloadStr = (p: Record<string, unknown> | null, key: string): string => (typeof p?.[key] === "string" ? (p![key] as string) : "");

// A secret pool is a list of topics: plain strings (Spyfall locations) or objects with
// per-role slots (Undercover word pairs). Render it as one topic per line — for keyed
// pools, the slot values joined by " | " in a stable key order.
const poolToText = (pool: unknown): { text: string; keys: string[] } => {
  if (!Array.isArray(pool) || pool.length === 0) {
    return { text: "", keys: [] };
  }
  if (pool.every((e) => typeof e === "string")) {
    return { text: (pool as string[]).join("\n"), keys: [] };
  }

  const keys: string[] = [];
  for (const e of pool) {
    if (e && typeof e === "object") {
      for (const k of Object.keys(e as Record<string, unknown>)) {
        if (!keys.includes(k)) keys.push(k);
      }
    }
  }
  const lines = pool.map((e) => keys.map((k) => String((e as Record<string, unknown>)?.[k] ?? "").trim()).join(" | "));

  return { text: lines.join("\n"), keys };
};

const textToPool = (text: string, keys: string[]): unknown[] => {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (keys.length === 0) {
    return lines;
  }

  return lines.map((line) => {
    const parts = line.split("|").map((s) => s.trim());
    const obj: Record<string, string> = {};
    keys.forEach((k, i) => {
      obj[k] = parts[i] ?? "";
    });
    return obj;
  });
};

// Split a stored payload into the editable text fields + the leftover scalar keys.
const payloadToDossier = (p: Record<string, unknown> | null | undefined) => {
  const extra: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(p || {})) {
    if (!DOSSIER_KEYS.includes(k) && !POOL_KEYS.includes(k)) extra[k] = v;
  }
  const { text: dSecretPool, keys: dSecretPoolKeys } = poolToText(p?.secretPool);

  return {
    dFlavor: payloadStr(p ?? null, "flavor"),
    dBackstory: payloadStr(p ?? null, "backstory"),
    dSecret: payloadStr(p ?? null, "secret"),
    dObjectives: listToLines(p?.objectives),
    dRelationships: listToLines(p?.relationships),
    dSecretPool,
    dSecretPoolKeys,
    hasPool: Array.isArray(p?.secretPool) && (p!.secretPool as unknown[]).length > 0,
    dIdentityPool: listToLines(p?.identityPool),
    hasIdentityPool: Array.isArray(p?.identityPool) && (p!.identityPool as unknown[]).length > 0,
    payloadExtra: Object.keys(extra).length ? extra : null,
  };
};

// Rebuild the marker.payload from the edited fields (+ preserved scalar keys), or null.
const dossierToPayload = (m: MarkerForm): Record<string, unknown> | null => {
  const next: Record<string, unknown> = { ...(m.payloadExtra || {}) };
  const flavor = m.dFlavor.trim();
  const backstory = m.dBackstory.trim();
  const secret = m.dSecret.trim();
  const objectives = linesToList(m.dObjectives);
  const relationships = linesToList(m.dRelationships);
  if (flavor) next.flavor = flavor;
  if (backstory) next.backstory = backstory;
  if (secret) next.secret = secret;
  if (objectives.length) next.objectives = objectives;
  if (relationships.length) next.relationships = relationships;

  const pool = textToPool(m.dSecretPool, m.dSecretPoolKeys);
  if (pool.length) next.secretPool = pool;

  const identityPool = linesToList(m.dIdentityPool);
  if (identityPool.length) next.identityPool = identityPool;

  return Object.keys(next).length ? next : null;
};

const formHasDossier = (m: MarkerForm): boolean =>
  !!(m.dFlavor.trim() || m.dBackstory.trim() || m.dSecret.trim() || m.dObjectives.trim() || m.dRelationships.trim() || m.payloadExtra);

const EMPTY_FORM: GameForm = { name: "", description: "", checklistText: "", checklistIds: [], allowReuse: false, preset: "", assignmentKind: "role", excludeHousehold: true, matchInterests: true, revealRule: "on_elimination", notes: "", markers: [] };

// ── Checklist template (Human Bingo / scavenger) helpers ─────────────────────
const slugId = (label: string, i: number): string =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 32) || `item-${i}`;

// A stored checklist_template → the editable text (one prompt per line) + its ids.
const templateToForm = (tpl: any): { checklistText: string; checklistIds: string[]; allowReuse: boolean } => {
  const items = Array.isArray(tpl?.items) ? tpl.items : [];
  return {
    checklistText: items.map((it: any) => String(it?.label ?? "")).join("\n"),
    checklistIds: items.map((it: any) => String(it?.id ?? "")),
    allowReuse: !!tpl?.allowReuse,
  };
};

// The editable text (+ position-matched ids) → a checklist_template, or null if empty.
const formToTemplate = (text: string, ids: string[], allowReuse: boolean): { items: { id: string; label: string }[]; allowReuse: boolean } | null => {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) {
    return null;
  }
  const used = new Set<string>();
  const items = lines.map((label, i) => {
    let id = ids[i] || slugId(label, i);
    while (used.has(id)) {
      id = `${id}-${i}`;
    }
    used.add(id);
    return { id, label };
  });

  return { items, allowReuse };
};

const compToForm = (c?: EventGameComposition | null): { compType: EventGameCompositionType; compValue: number } => {
  const type = (c?.type ?? "rest") as EventGameCompositionType;
  if (type === "ratio") return { compType: "ratio", compValue: Math.round((Number(c?.value) || 0) * 100) };
  if (type === "fixed") return { compType: "fixed", compValue: Math.max(0, Math.round(Number(c?.value) || 1)) };
  // bracket has no editor yet — surface it as "rest" so the row is still editable.
  return { compType: type === "bracket" ? "rest" : type, compValue: 0 };
};

const markerToForm = (m: EventGameMarker): MarkerForm => ({
  id: m.id,
  name: m.name ?? "",
  color: m.color ?? "#6d4aff",
  icon: m.icon ?? "",
  team: m.team ?? "",
  visibility: (m.visibility ?? "self") as EventGameMarkerVisibility,
  ...payloadToDossier(m.payload as Record<string, unknown> | null),
  ...compToForm(m.composition),
});

const presetToMarkerForm = (m: Omit<EventGameMarker, "id" | "gameId" | "eventId">): MarkerForm => ({
  name: m.name ?? "",
  color: m.color ?? "#6d4aff",
  icon: m.icon ?? "",
  team: m.team ?? "",
  visibility: (m.visibility ?? "self") as EventGameMarkerVisibility,
  ...payloadToDossier(m.payload as Record<string, unknown> | null),
  ...compToForm(m.composition),
});

const formToComposition = (m: MarkerForm): EventGameComposition => {
  if (m.compType === "fixed") return { type: "fixed", value: Math.max(0, Math.round(m.compValue || 0)) };
  if (m.compType === "ratio") return { type: "ratio", value: Math.min(1, Math.max(0, (m.compValue || 0) / 100)) };
  return { type: "rest" };
};

// Applying a preset prefills the name (if blank), reveal rule + roles. Pure, so the open-effect can seed the snapshot
// (#123) with exactly the form a `defaultPresetId` produces, and the picker applies it the same way.
const withPreset = (f: GameForm, presetId: string): GameForm => {
  const preset = findPreset(presetId);

  return {
    ...f,
    preset: presetId,
    assignmentKind: preset?.assignmentKind ?? "role",
    excludeHousehold: preset?.winRules?.excludeHousehold !== false,
    matchInterests: preset?.winRules?.matchInterests !== false,
    revealRule: preset ? preset.revealRule : f.revealRule,
    name: f.name.trim() ? f.name : preset?.label ?? f.name,
    markers: preset ? preset.markers.map(presetToMarkerForm) : f.markers,
    ...(preset ? templateToForm(preset.checklistTemplate ?? null) : {}),
  };
};

export const GameDrawer: React.FC<GameDrawerProps> = ({ opened, onClose, eventId, game, onSaved, defaultPresetId = null }) => {
  const { t } = useTranslation(["events", "common"]);

  const [form, setForm] = useState<GameForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // The submit's OWN OpResult, kept at component scope. Mapping it inside the
  // save handler threw the per-field messages away the moment the handler
  // returned, so a server refusal on name/description was never rendered.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  // Role-sync failures: plain strings from a second round of calls, not an OpResult.
  const [markerProblems, setMarkerProblems] = useState<string[]>([]);
  const [openDossiers, setOpenDossiers] = useState<Record<number, boolean>>({});
  const [openPools, setOpenPools] = useState<Record<number, boolean>>({});
  const seededRef = useRef<string | null>(null);

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(submitError, KNOWN_FORM_FIELDS),
    [submitError]
  );

  // The footer alert shows both the server's non-field messages and any role-sync problems.
  const generic = useMemo(() => [...genericErrors, ...markerProblems], [genericErrors, markerProblems]);

  const toggleDossier = useCallback((idx: number) => {
    setOpenDossiers((o) => ({ ...o, [idx]: !o[idx] }));
  }, []);

  const togglePool = useCallback((idx: number) => {
    setOpenPools((o) => ({ ...o, [idx]: !o[idx] }));
  }, []);

  // Esc / a click outside / Cancel ask before discarding a changed game (#123).
  const { isDirty, seedFrom, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      seededRef.current = null;
      clear();

      return;
    }
    const key = game?.id ?? "__new__";
    if (seededRef.current === key) {
      return;
    }
    seededRef.current = key;
    if (game) {
      const seeded: GameForm = {
        name: game.name ?? "",
        description: game.description ?? "",
        ...templateToForm(game.checklistTemplate),
        preset: game.preset ?? "",
        assignmentKind: (game.assignmentKind ?? "role") as EventGameAssignmentKind,
        excludeHousehold: (game.winRules as { excludeHousehold?: boolean } | null)?.excludeHousehold !== false,
        matchInterests: (game.winRules as { matchInterests?: boolean } | null)?.matchInterests !== false,
        revealRule: (game.revealRule ?? "on_elimination") as EventGameRevealRule,
        notes: game.notes ?? "",
        markers: Array.isArray(game.markers) ? [...game.markers].sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0)).map(markerToForm) : [],
      };
      setForm(seeded);
      seedFrom(seeded);
    } else {
      // A default preset applies on top of the empty form — exactly as picking it in the drawer would — and is part of
      // the snapshot: the host did not choose it here, so closing it untouched asks nothing.
      const seeded = defaultPresetId ? withPreset(EMPTY_FORM, defaultPresetId) : EMPTY_FORM;
      setForm(seeded);
      seedFrom(seeded);
    }
    setSubmitError(null);
    setMarkerProblems([]);
    setOpenDossiers({});
    setOpenPools({});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seeded once per open (seededRef); clear / seedFrom are stable
  }, [opened, game, defaultPresetId]);

  const set = useCallback(<K extends keyof GameForm>(key: K, value: GameForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const setMarker = useCallback((idx: number, patch: Partial<MarkerForm>) => {
    setForm((f) => ({ ...f, markers: f.markers.map((m, i) => (i === idx ? { ...m, ...patch } : m)) }));
  }, []);

  const addMarker = useCallback(() => {
    setForm((f) => ({ ...f, markers: [...f.markers, { ...EMPTY_MARKER }] }));
  }, []);

  const removeMarker = useCallback((idx: number) => {
    setForm((f) => ({ ...f, markers: f.markers.filter((_, i) => i !== idx) }));
  }, []);

  // Only offered on a NEW game so it never silently wipes a configured game's roles.
  const applyPreset = useCallback((presetId: string) => {
    setForm((f) => withPreset(f, presetId));
  }, []);

  const presetOptions = useMemo(
    () => [
      { value: "", label: t("events:games.presetCustom", { defaultValue: "Custom (build your own)" }) },
      ...GAME_PRESETS.map((p) => ({ value: p.id, label: p.label })),
    ],
    [t]
  );

  const visibilityOptions = useMemo(
    () => [
      { value: "self", label: t("events:games.visSelf", { defaultValue: "Secret — only the player" }) },
      { value: "others", label: t("events:games.visOthers", { defaultValue: "Others see it, not the player" }) },
      { value: "all", label: t("events:games.visAll", { defaultValue: "Public — everyone" }) },
    ],
    [t]
  );

  const revealOptions = useMemo(
    () => [
      { value: "on_elimination", label: t("events:games.revealOnElim", { defaultValue: "When a player is out" }) },
      { value: "at_end", label: t("events:games.revealAtEnd", { defaultValue: "When the round ends" }) },
      { value: "never", label: t("events:games.revealNever", { defaultValue: "Never" }) },
    ],
    [t]
  );

  const compTypeOptions = useMemo(
    () => [
      { value: "fixed", label: t("events:games.compFixed", { defaultValue: "Fixed count" }) },
      { value: "ratio", label: t("events:games.compRatio", { defaultValue: "Share of players (%)" }) },
      { value: "rest", label: t("events:games.compRest", { defaultValue: "Fill the rest" }) },
    ],
    [t]
  );

  const syncMarkers = useCallback(
    async (gameId: string, existing: EventGameMarker[]): Promise<string[]> => {
      const problems: string[] = [];
      const rows = form.markers.filter((m) => m.name.trim());

      // Removes: existing rows no longer present in the form.
      const keepIds = new Set(rows.map((m) => m.id).filter(Boolean) as string[]);
      const toRemove = existing.filter((e) => e.id && !keepIds.has(e.id)).map((e) => e.id as string);
      if (toRemove.length > 0) {
        const res = await EventGameMarkerService.remove(toRemove);
        if (res.code !== OP_RESULT_CODES.OK) problems.push(refusalReason(res) || "Could not remove a role.");
      }

      // Creates + updates — a few at a time, and none sent once the server stops answering (ROADMAP #131).
      const results = await runBulkWrites(rows, (m, i) => {
        const params: EventGameMarkerInput = {
          name: m.name.trim(),
          color: m.color || null,
          icon: m.icon.trim() || null,
          team: m.team.trim() || null,
          visibility: m.visibility,
          composition: formToComposition(m),
          payload: dossierToPayload(m),
          orderNo: (i + 1) * 1000,
        };

        return m.id ? EventGameMarkerService.update(m.id, params) : EventGameMarkerService.create({ ...params, gameId });
      });
      results.forEach((res, i) => {
        if (res.code !== OP_RESULT_CODES.OK) {
          problems.push(refusalReason(res) || `Could not save role “${rows[i].name}”.`);
        }
      });

      return problems;
    },
    [form.markers]
  );

  const handleSave = useCallback(async () => {
    setSubmitError(null);
    setMarkerProblems([]);

    const isTarget = form.assignmentKind === "target";
    const isPair = form.assignmentKind === "pair";
    const params: EventGameInput = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      preset: form.preset || null,
      assignmentKind: form.assignmentKind,
      revealRule: form.revealRule,
      winRules: isTarget
        ? { excludeHousehold: form.excludeHousehold }
        : isPair
          ? { excludeHousehold: form.excludeHousehold, matchInterests: form.matchInterests }
          : null,
      notes: form.notes.trim() || null,
      checklistTemplate: formToTemplate(form.checklistText, form.checklistIds, form.allowReuse),
    };

    setSaving(true);
    const result = game?.id ? await EventGameService.update(game.id, params) : await EventGameService.create({ ...params, eventId });

    if (result.code !== OP_RESULT_CODES.OK) {
      setSaving(false);
      // Keep the OpResult itself: the component-scope mapping puts each field
      // message under its input and the rest into the footer alert.
      setSubmitError(result);

      return;
    }

    const savedGame = (Array.isArray(result.data) ? result.data[0] : null) as EventGame | null;
    const gameId = savedGame?.id || game?.id;
    const problems = gameId ? await syncMarkers(gameId, Array.isArray(game?.markers) ? (game!.markers as EventGameMarker[]) : []) : [];
    setSaving(false);

    if (problems.length > 0) {
      setMarkerProblems(problems);
      onSaved(); // the game (and any roles that did save) are persisted — refresh so the UI reflects it.

      return;
    }

    onSaved();
    onClose();
  }, [form, game, eventId, syncMarkers, onSaved, onClose]);

  // `getFormErrors` pre-fills every known field with undefined, so a keys check
  // would always be true — only a truthy VALUE means a field is flagged.
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);
  // Target-kind games (Secret Santa) deal another PLAYER, not a role; pair games (Networking
  // Roulette) match two players. Both hide the roles editor + reveal rule + checklist and show
  // their own options (household rule; + interest matching for pair games).
  const isTarget = form.assignmentKind === "target";
  const isPair = form.assignmentKind === "pair";
  // A target game with elimination is Assassin (vs Secret Santa, reveal "never") — same options,
  // different copy, so the config panel isn't mislabeled "Secret Santa" for an Assassin game.
  const isAssassinCfg = isTarget && form.revealRule === "on_elimination";
  const hidesRoles = isTarget || isPair;
  // Show the checklist editor for checklist-mode games (the Human Bingo preset, or any
  // game that already has a card). Stays visible once seeded even if the text is cleared.
  const showChecklist = !hidesRoles && (form.preset === "humanbingo" || form.checklistText.trim().length > 0 || form.checklistIds.length > 0);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={game ? t("events:games.editGame", { defaultValue: "Edit game" }) : t("events:games.addGame", { defaultValue: "Add game" })}
      size="xl"
      loading={saving}
      errors={generic}
      hasFieldErrors={hasFieldErrors}
      actionType="save-cancel"
      confirmShortcut="⌘S"
      onConfirm={handleSave}
      isDirty={isDirty}
    >
      <Stack gap="md">
        {!game && (
          <SelectField
            name="preset"
            label={t("events:games.presetLabel", { defaultValue: "Start from a preset" })}
            description={t("events:games.presetHint", { defaultValue: "Prefills the roles — you can tweak them below." })}
            data={presetOptions}
            value={form.preset}
            onChange={(_n, v) => applyPreset(v ?? "")}
            allowDeselect={false}
          />
        )}

        <TextField
          name="name"
          label={t("events:games.nameLabel", { defaultValue: "Game name" })}
          placeholder={t("events:games.namePlaceholder", { defaultValue: "e.g. Reception Mafia" })}
          value={form.name}
          onChange={(_n, v) => set("name", v ?? "")}
          error={fieldErrors.name}
          required
          data-autofocus
        />

        <TextAreaField
          name="description"
          label={t("events:games.descriptionLabel", { defaultValue: "Description" })}
          placeholder={t("events:games.descriptionPlaceholder", { defaultValue: "How to play, when it runs…" })}
          value={form.description}
          onChange={(_n, v) => set("description", v ?? "")}
          autosize
          minRows={2}
          error={fieldErrors.description}
        />

        {isTarget && (
          <Paper withBorder radius="md" p="md" bg="var(--mantine-color-red-light)">
            <Stack gap="sm">
              <Group gap={8} wrap="nowrap">
                <AppIcon iconName={isAssassinCfg ? "Target" : "Gift"} size={18} />
                <Text size="md" fw={600}>
                  {isAssassinCfg
                    ? t("events:games.assassinSection", { defaultValue: "Assassin" })
                    : t("events:games.secretSantaSection", { defaultValue: "Secret Santa" })}
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {isAssassinCfg
                  ? t("events:games.assassinHint", {
                      defaultValue: "Every player secretly draws a target to “eliminate”. When someone is caught they self-report on their own device and their assassin inherits the next target — last one standing wins. Deal it once from the Play screen.",
                    })
                  : t("events:games.secretSantaHint", {
                      defaultValue: "Every player secretly draws another guest to gift — nobody draws themselves, and each player privately sees who they got. Deal it once from the Play screen.",
                    })}
              </Text>

              <Switch
                checked={form.excludeHousehold}
                onChange={(e) => set("excludeHousehold", e.currentTarget.checked)}
                label={t("events:games.excludeHouseholdLabel", { defaultValue: "Keep households apart" })}
                description={t("events:games.excludeHouseholdHint", {
                  defaultValue: "On = no one draws a guest from their own party/RSVP group (so partners and families don't get each other). Turn off if the group is too small to match.",
                })}
              />

              <TextAreaField
                name="giftNote"
                label={isAssassinCfg
                  ? t("events:games.assassinNoteLabel", { defaultValue: "How to “eliminate” (optional)" })
                  : t("events:games.giftNoteLabel", { defaultValue: "Note for players (optional)" })}
                description={isAssassinCfg
                  ? t("events:games.assassinNoteHint", { defaultValue: "Shown to each player under their target — e.g. the tag / secret task that counts as an elimination." })
                  : t("events:games.giftNoteHint", { defaultValue: "Shown to each player under the guest they drew — e.g. a gift budget or theme." })}
                placeholder={isAssassinCfg
                  ? t("events:games.assassinNotePh", { defaultValue: "e.g. hand them a sticker without them noticing." })
                  : t("events:games.giftNotePh", { defaultValue: "Keep it a secret! Budget around $20." })}
                value={form.markers[0]?.dFlavor ?? ""}
                onChange={(_n, v) => setMarker(0, { dFlavor: v ?? "" })}
                autosize
                minRows={2}
              />
            </Stack>
          </Paper>
        )}

        {isPair && (
          <Paper withBorder radius="md" p="md" bg="var(--mantine-color-cyan-light)">
            <Stack gap="sm">
              <Group gap={8} wrap="nowrap">
                <AppIcon iconName="Users" size={18} />
                <Text size="md" fw={600}>
                  {t("events:games.networkingSection", { defaultValue: "Networking Roulette" })}
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {t("events:games.networkingHint", {
                  defaultValue: "Each round, every guest is matched with a new person to meet (a trio for an odd count). Deal a fresh round from the Play screen to reshuffle partners — nobody is paired twice until everyone has met.",
                })}
              </Text>

              <Switch
                checked={form.matchInterests}
                onChange={(e) => set("matchInterests", e.currentTarget.checked)}
                label={t("events:games.matchInterestsLabel", { defaultValue: "Match on shared interests" })}
                description={t("events:games.matchInterestsHint", {
                  defaultValue: "On = prefer partners who share interests (each guest lists theirs on their own card), falling back to random. Off = purely random partners.",
                })}
              />

              <Switch
                checked={form.excludeHousehold}
                onChange={(e) => set("excludeHousehold", e.currentTarget.checked)}
                label={t("events:games.excludeHouseholdLabelPair", { defaultValue: "Keep households apart" })}
                description={t("events:games.excludeHouseholdHintPair", {
                  defaultValue: "On = don't pair two guests from the same party/RSVP group (they already know each other). Turn off if the group is too small to match.",
                })}
              />
            </Stack>
          </Paper>
        )}

        {!hidesRoles && (
          <SelectField
            name="revealRule"
            label={t("events:games.revealLabel", { defaultValue: "Reveal secret roles" })}
            description={t("events:games.revealHint", { defaultValue: "When a hidden role becomes public." })}
            data={revealOptions}
            value={form.revealRule}
            onChange={(_n, v) => set("revealRule", (v as EventGameRevealRule) || "on_elimination")}
            allowDeselect={false}
          />
        )}

        {!hidesRoles && <Divider label={t("events:games.rolesSection", { defaultValue: "Roles" })} labelPosition="left" />}

        {!hidesRoles && form.markers.length === 0 && (
          <Text size="md" c="dimmed">
            {t("events:games.noRoles", { defaultValue: "No roles yet. Pick a preset above, or add roles below." })}
          </Text>
        )}

        {!hidesRoles && (
        <Stack gap="sm">
          {form.markers.map((m, i) => (
            <Paper key={i} withBorder radius="md" p="sm">
              <Stack gap={8}>
                <Group align="flex-end" gap="xs" wrap="nowrap">
                  <Box style={{ flex: 1 }}>
                    <TextField
                      name={`role-name-${i}`}
                      label={i === 0 ? t("events:games.roleNameLabel", { defaultValue: "Role" }) : undefined}
                      placeholder={t("events:games.roleNamePh", { defaultValue: "e.g. Mafia" })}
                      value={m.name}
                      onChange={(_n, v) => setMarker(i, { name: v ?? "" })}
                    />
                  </Box>
                  <Box w={110}>
                    <TextField
                      name={`role-icon-${i}`}
                      label={i === 0 ? t("events:games.roleIconLabel", { defaultValue: "Icon" }) : undefined}
                      placeholder="🔪"
                      value={m.icon}
                      onChange={(_n, v) => setMarker(i, { icon: v ?? "" })}
                    />
                  </Box>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    mb={6}
                    onClick={() => removeMarker(i)}
                    aria-label={t("common:buttons.remove", { defaultValue: "Remove" })}
                  >
                    <AppIcon iconName="Trash" size={16} />
                  </ActionIcon>
                </Group>

                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                  <SelectField
                    name={`role-vis-${i}`}
                    label={t("events:games.roleVisLabel", { defaultValue: "Who sees it" })}
                    data={visibilityOptions}
                    value={m.visibility}
                    onChange={(_n, v) => setMarker(i, { visibility: (v as EventGameMarkerVisibility) || "self" })}
                    allowDeselect={false}
                  />
                  <ColorPickerInput
                    name={`role-color-${i}`}
                    label={t("events:games.roleColorLabel", { defaultValue: "Color" })}
                    value={m.color}
                    onChange={(_n, v) => setMarker(i, { color: v })}
                  />
                </SimpleGrid>

                <SimpleGrid cols={{ base: 1, sm: m.compType === "rest" ? 2 : 3 }} spacing="xs">
                  <SelectField
                    name={`role-comp-${i}`}
                    label={t("events:games.roleCountLabel", { defaultValue: "How many" })}
                    data={compTypeOptions}
                    value={m.compType}
                    onChange={(_n, v) => setMarker(i, { compType: (v as EventGameCompositionType) || "rest" })}
                    allowDeselect={false}
                  />
                  {m.compType !== "rest" && (
                    <NumberField
                      name={`role-compval-${i}`}
                      label={m.compType === "ratio" ? t("events:games.roleRatioLabel", { defaultValue: "% of players" }) : t("events:games.roleFixedLabel", { defaultValue: "Count" })}
                      value={m.compValue}
                      onChange={(_n, v) => setMarker(i, { compValue: typeof v === "number" ? v : 0 })}
                      min={0}
                      max={m.compType === "ratio" ? 100 : 999}
                    />
                  )}
                  <TextField
                    name={`role-team-${i}`}
                    label={t("events:games.roleTeamLabel", { defaultValue: "Team (optional)" })}
                    placeholder={t("events:games.roleTeamPh", { defaultValue: "e.g. town" })}
                    value={m.team}
                    onChange={(_n, v) => setMarker(i, { team: v ?? "" })}
                  />
                </SimpleGrid>

                <Box>
                  <UnstyledButton onClick={() => toggleDossier(i)} aria-expanded={!!openDossiers[i]}>
                    <Group gap={6} wrap="nowrap">
                      <AppIcon iconName={openDossiers[i] ? "ChevronDown" : "ChevronRight"} size={14} />
                      <Text size="sm" c="dimmed">
                        {t("events:games.dossierToggle", { defaultValue: "Character dossier (optional)" })}
                      </Text>
                      {formHasDossier(m) && <AppIcon iconName="Check" size={13} />}
                    </Group>
                  </UnstyledButton>

                  <Collapse in={!!openDossiers[i]}>
                    <Stack gap="xs" mt="xs">
                      <TextField
                        name={`role-flavor-${i}`}
                        label={t("events:games.dossierFlavorLabel", { defaultValue: "Hook (one line)" })}
                        placeholder={t("events:games.dossierFlavorPh", { defaultValue: "A short teaser shown under the role name" })}
                        value={m.dFlavor}
                        onChange={(_n, v) => setMarker(i, { dFlavor: v ?? "" })}
                      />
                      <TextAreaField
                        name={`role-backstory-${i}`}
                        label={t("events:games.dossierBackstoryLabel", { defaultValue: "Backstory" })}
                        value={m.dBackstory}
                        onChange={(_n, v) => setMarker(i, { dBackstory: v ?? "" })}
                        autosize
                        minRows={2}
                      />
                      <TextAreaField
                        name={`role-secret-${i}`}
                        label={t("events:games.dossierSecretLabel", { defaultValue: "Their secret" })}
                        value={m.dSecret}
                        onChange={(_n, v) => setMarker(i, { dSecret: v ?? "" })}
                        autosize
                        minRows={2}
                      />
                      <TextAreaField
                        name={`role-objectives-${i}`}
                        label={t("events:games.dossierObjectivesLabel", { defaultValue: "Objectives (one per line)" })}
                        value={m.dObjectives}
                        onChange={(_n, v) => setMarker(i, { dObjectives: v ?? "" })}
                        autosize
                        minRows={2}
                      />
                      <TextAreaField
                        name={`role-relationships-${i}`}
                        label={t("events:games.dossierRelationshipsLabel", { defaultValue: "Relationships (one per line)" })}
                        value={m.dRelationships}
                        onChange={(_n, v) => setMarker(i, { dRelationships: v ?? "" })}
                        autosize
                        minRows={2}
                      />
                    </Stack>
                  </Collapse>
                </Box>

                {m.hasPool && (
                  <Box>
                    <UnstyledButton onClick={() => togglePool(i)} aria-expanded={!!openPools[i]}>
                      <Group gap={6} wrap="nowrap">
                        <AppIcon iconName={openPools[i] ? "ChevronDown" : "ChevronRight"} size={14} />
                        <Text size="sm" c="dimmed">
                          {t("events:games.poolToggle", {
                            defaultValue: "{{label}} — {{count}} option(s)",
                            label: (m.payloadExtra?.secretLabel as string) || t("events:games.poolDefaultLabel", { defaultValue: "Secret pool" }),
                            count: linesToList(m.dSecretPool).length,
                          })}
                        </Text>
                      </Group>
                    </UnstyledButton>

                    <Collapse in={!!openPools[i]}>
                      <Stack gap={4} mt="xs">
                        <TextAreaField
                          name={`role-pool-${i}`}
                          label={
                            m.dSecretPoolKeys.length > 0
                              ? t("events:games.poolKeyedLabel", { defaultValue: "One per line — values in order: {{keys}}", keys: m.dSecretPoolKeys.join(" | ") })
                              : t("events:games.poolLabel", { defaultValue: "One option per line" })
                          }
                          description={t("events:games.poolHint", {
                            defaultValue: "One is chosen at random each round. Players see only their own; the odd-one-out (Spy / Undercover) sees none of it.",
                          })}
                          value={m.dSecretPool}
                          onChange={(_n, v) => setMarker(i, { dSecretPool: v ?? "" })}
                          autosize
                          minRows={4}
                        />
                      </Stack>
                    </Collapse>
                  </Box>
                )}

                {(m.hasIdentityPool || m.visibility === "others") && (
                  <Box>
                    <TextAreaField
                      name={`role-identities-${i}`}
                      label={t("events:games.identityPoolLabel", { defaultValue: "Identities — one per line" })}
                      description={t("events:games.identityPoolHint", {
                        defaultValue: "Each player is dealt a different one at random (they can’t see their own; everyone else can). Add at least as many as you have players.",
                      })}
                      value={m.dIdentityPool}
                      onChange={(_n, v) => setMarker(i, { dIdentityPool: v ?? "" })}
                      autosize
                      minRows={4}
                    />
                  </Box>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
        )}

        {!hidesRoles && (
          <AppButton variant="secondary" iconName="Plus" onClick={addMarker}>
            {t("events:games.addRole", { defaultValue: "Add role" })}
          </AppButton>
        )}

        {showChecklist && (
          <>
            <Divider label={t("events:games.checklistSection", { defaultValue: "Bingo / scavenger card" })} labelPosition="left" />

            <TextAreaField
              name="checklist"
              label={t("events:games.checklistLabel", { defaultValue: "Squares — one per line" })}
              description={t("events:games.checklistHint", {
                defaultValue: "Each guest gets this same card and marks a square when they find a matching guest (by scanning their code or typing their name).",
              })}
              value={form.checklistText}
              onChange={(_n, v) => set("checklistText", v ?? "")}
              autosize
              minRows={5}
            />

            <Switch
              checked={form.allowReuse}
              onChange={(e) => set("allowReuse", e.currentTarget.checked)}
              label={t("events:games.allowReuseLabel", { defaultValue: "Allow the same guest for more than one square" })}
              description={t("events:games.allowReuseHint", {
                defaultValue: "Off = each square must be a different person (forces mingling). Turn on for small parties.",
              })}
            />
          </>
        )}

        <Divider />

        <TextAreaField
          name="notes"
          label={t("events:games.notesLabel", { defaultValue: "Notes (private)" })}
          value={form.notes}
          onChange={(_n, v) => set("notes", v ?? "")}
          autosize
          minRows={2}
        />
      </Stack>
    </AppDrawer>
  );
};
